import React from "react"
import Table from "../common/Table"
import { TableHeaderColumn, TableRow, TableHeader, TableRowColumn, TableBody, Avatar, FontIcon } from "material-ui"
import strings from "../../localization/strings"
import { IPosition } from "../../models"
import AccountStore from "../../stores/AccountStore"
import { green500 } from "material-ui/styles/colors"
import * as permissionHelper from "../../helpers/permissionHelper"
import AuthenticationStore from "../../stores/AuthenticationStore"

interface IProps {
	readonly?: boolean
	hideBill?: boolean
	positions: IPosition[]
	selectedPositions: IPosition[]
	isDialog: boolean
	handleEdit?(editPosition: IPosition): void
	handleSelect(selectedPositions: IPosition[]): void
}

const PositionsTable: React.SFC<IProps> = (props: IProps) => {
	const formatter = new Intl.NumberFormat('en-US', {
		style: 'currency',
		currency: 'USD',
	})
	return (
		<Table
			items={props.positions}
			selectedItems={props.selectedPositions}
			handleClickRow={props.handleEdit}
			handleSelect={props.handleSelect}
		>
			<TableHeader
				displaySelectAll={!props.readonly}
				enableSelectAll={!props.readonly}
				adjustForCheckbox={!props.readonly}
			>
				<TableRow className="ShortSelectBoxTableHeader">
					{!props.isDialog &&
						<TableHeaderColumn style={styles.colorValue}></TableHeaderColumn>
					}
					<TableHeaderColumn className="TextOverflow" title={strings.Name} style={styles.columnValue}>{strings.Name}</TableHeaderColumn>
					<TableHeaderColumn className="TextOverflow" title={strings.CostCenter} style={styles.columnValue}>{strings.CostCenter}</TableHeaderColumn>
					{!props.isDialog && [
						<TableHeaderColumn key="Description" className="TextOverflow" title={strings.Description} style={styles.columnValue}>{strings.Description}</TableHeaderColumn>,
						<TableHeaderColumn key="Hide_On_Mobile" className="TextOverflow" title={strings.Hide_On_Mobile} style={styles.columnValueCenter}>{strings.Hide_On_Mobile}</TableHeaderColumn>
					]}
					{permissionHelper.hasPermission(AuthenticationStore.Permissions.Account.ViewRates) && [
						<TableHeaderColumn key="pay_type" className="TextOverflow" title={strings.Pay_Rate} style={styles.columnValueCenter}>{strings.Pay_Rate}</TableHeaderColumn>,
						!props.hideBill &&
						<TableHeaderColumn key="bill_type" className="TextOverflow" title={strings.Bill_Rate} style={styles.columnValueCenter}>{strings.Bill_Rate}</TableHeaderColumn>
					]}
					<TableHeaderColumn className="TextOverflow" title={strings.Pay_Type} style={{ ...styles.columnValueCenter, width: props.isDialog && 150 }}>{strings.Pay_Type}</TableHeaderColumn>
					
					
					{!props.isDialog && AccountStore.getPayClassEnabled() &&
						<TableHeaderColumn className="TextOverflow" title={strings.Use_Pay_Classes} style={styles.columnValueCenter}>{strings.Use_Pay_Classes}</TableHeaderColumn>
					}
					{!props.isDialog && [
						<TableHeaderColumn key="RequiredTasks" className="TextOverflow" title={strings.RequiredTasks} style={styles.columnValueCenter}>{strings.RequiredTasks}</TableHeaderColumn>,
						<TableHeaderColumn key="OptionalTasks" className="TextOverflow" title={strings.OptionalTasks} style={styles.columnValueCenter}>{strings.OptionalTasks}</TableHeaderColumn>
					]}
				</TableRow>
			</TableHeader>
			<TableBody
				showRowHover={true}
				deselectOnClickaway={false}
				displayRowCheckbox={!props.readonly}
			>
				{props.positions.map((row, i) => {
					const optLength = (row.OptionalTasks && row.OptionalTasks.length) || 0
					const reqLength = (row.RequiredTasks && row.RequiredTasks.length) || 0
					const optionalTasksMessage = strings.formatString((optLength === 1 && strings.Task) || strings.x_Tasks, optLength)
					const requiredTasksMessage = strings.formatString((reqLength === 1 && strings.Task) || strings.x_Tasks, reqLength)
					const payType = AccountStore.getCurrentAccount().PayTypes.find(item => item.Id === row.PayTypeId)
					const usePayClasses = row.DefaultPayClass ? true : false
					return (
						<TableRow
							data-intercom-target={i === 0 && "Postions-openPostionDetails"}
							key={i}
							className="ShortSelectBoxTableRow"
							selected={props.selectedPositions.find(item => item.Id === row.Id) ? true : false}
						>
							{!props.isDialog &&
								<TableRowColumn style={{ ...styles.colorValue, paddingTop: props.readonly && 10 }}>
									<Avatar backgroundColor={row.Color ? row.Color : AccountStore.getPositionDefaultColor()} size={30} />
								</TableRowColumn>
							}
							<TableRowColumn style={styles.columnValue}>{row.Name}</TableRowColumn>
							<TableRowColumn style={styles.columnValue}>{row.CostCenter}</TableRowColumn>
							{!props.isDialog && [
								<TableRowColumn key="Description" style={styles.columnValue}>{row.Description}</TableRowColumn>,
								<TableRowColumn key="Hide_On_Mobile" style={styles.columnValueCenter}>
									{row.HideOnMobile && <FontIcon className="fa fa-check" style={{ fontSize: 18, color: green500 }} />}
								</TableRowColumn>
							]}
							{permissionHelper.hasPermission(AuthenticationStore.Permissions.Account.ViewRates) && [
								<TableRowColumn key="pay_rate" style={styles.columnValueCenter}>{(row.PayInfo && formatter.format(row.PayInfo.PayRate)) || <span>-</span>}</TableRowColumn>,
								!props.hideBill &&
								<TableRowColumn key="bill_rate" style={styles.columnValueCenter}>{(row.PayInfo && formatter.format(row.PayInfo.BillRate)) || <span>-</span>}</TableRowColumn>
							]}
							<TableRowColumn style={{ ...styles.columnValueCenter, width: props.isDialog && 150 }}>{payType && payType.Name}</TableRowColumn>
							{!props.isDialog && AccountStore.getPayClassEnabled() &&
								<TableRowColumn style={styles.columnValueCenter}>
									{usePayClasses &&
										<FontIcon className="fa fa-check" style={{ fontSize: 18, color: green500 }} />
									}
								</TableRowColumn>
							}
							{!props.isDialog && [
								<TableRowColumn key="RequiredTasks" style={styles.columnValueCenter}>{requiredTasksMessage}</TableRowColumn>,
								<TableRowColumn key="OptionalTasks" style={styles.columnValueCenter}>{optionalTasksMessage}</TableRowColumn>
							]}
						</TableRow>
					)
				})}
			</TableBody>
		</Table >
	)
}

export default PositionsTable

const styles = {
	read: {
		fontWeight: "normal",
		cursor: "pointer"
	},
	unRead: {
		fontWeight: "bold",
		cursor: "pointer"
	},
	columnValue: {
		textAlign: "left",
		cursor: "pointer",
		whitespace: "normal",
		paddingLeft: 5,
		paddingRight: 5
	},
	columnValueCenter: {
		textAlign: "center",
		cursor: "pointer",
		whitespace: "normal",
		paddingLeft: 5,
		paddingRight: 5
	},
	colorValue: {
		textAlign: "center",
		cursor: "pointer",
		whitespace: "normal",
		width: 35,
		padding: 0,
	}
}